// 音まわりの設定（2026-09-20・実機の気づいたこと2
// 「サウンド音量、BGM On/Off を設定できる設定画面を追加。トップ画面に設定ボタン配置」）。
//
// ## 🔴 ここは**ポータル横断の設定**しか扱わない
//
// `hongsoft.*`（音量・ミュート・BGM）＝どのゲームでも同じ意味を持つものだけ。
// ⚠️ ゲーム固有の設定（`<key>.*`）はゲームの中の設定へ置く
// （[settings.ts](../net/settings.ts) の「設定は所属で2つに分かれる」）。
//
// ## 🔴 なぜ DOM で作るのか
//
// 各ゲームは Phaser の設定ダイアログを持っている（[samegame の例](../../games/samegame/scenes/ui/settingsDialog.ts)）が、
// ⚠️ **ポータルとポーカーの卓は Phaser を読み込まない**。1.2MB を設定画面のために
// 呼ぶ理由が無いので、ここは他のモーダルと同じ純 DOM で組む。
//
// ## ⚠️ 音量は**つまみを動かしたその場で効く**
//
// 効果音は鳴らすたびに `getSoundVolume()` を見るので黙っていても効くが、
// 🔴 **もう鳴っている BGM は直らない**。⇒ 呼び出し側が `onChange` で直す
// （ポーカーの卓は `refreshBgm()` を渡す）。

import { t } from '@/platform/i18n';
import {
  getSoundVolume,
  isBgmOn,
  isSoundMuted,
  pushSettingsToServer,
  setBgmOn,
  setSoundMuted,
  setSoundVolume,
} from '@/platform/net/settings';
import { el, openOverlay } from './modalChrome';

export interface SettingsModalOptions {
  /**
   * 設定が変わるたびに呼ぶ。**鳴っている音を直す**ために使う。
   * ⚠️ つまみを動かしている最中も呼ばれる（毎フレームではないが連続する）ので、重い処理を置かない。
   */
  onChange?: () => void;
  /**
   * 音量を試すための音。⚠️ **呼び出し側が持つ**——ここで素材を指すと、
   * ポータルが（音を1つも持っていないのに）ポーカーの素材に依存することになる。
   */
  preview?: () => void;
}

/** 設定モーダルを開く。閉じると解決する。 */
export function openSettingsModal(opts: SettingsModalOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    const { overlay, panel, close } = openOverlay();

    const title = el('div', 'hs-modal-title', t('portal.settings.title'));
    const hint = el('div', 'hs-modal-hint', t('portal.settings.hint'));

    // --- 音量 ---
    const volRow = el('div', 'hs-set-row');
    const volLabel = el('label', 'hs-set-label', t('portal.settings.volume'));
    const vol = document.createElement('input');
    vol.type = 'range';
    vol.min = '0';
    vol.max = '100';
    vol.step = '5';
    vol.className = 'hs-set-range';
    vol.id = 'hs-set-vol';
    volLabel.setAttribute('for', vol.id);
    // 🔴 **ミュートと音量0を同じ扱いにする**（[audio.ts](../poker/audio.ts) の `audible` と揃える）。
    //    ⚠️ つまみが 0 なのに「ミュートではない」状態を作ると、鳴らない理由が2つになる。
    vol.value = String(Math.round((isSoundMuted() ? 0 : getSoundVolume()) * 100));
    const volValue = el('span', 'hs-set-value', `${vol.value}`);
    volRow.append(volLabel, vol, volValue);

    // --- BGM ---
    const bgmRow = el('div', 'hs-set-row');
    const bgmLabel = el('span', 'hs-set-label', t('portal.settings.bgm'));
    const bgmBtn = document.createElement('button');
    bgmBtn.type = 'button';
    bgmBtn.className = 'hs-btn hs-btn-ghost hs-set-toggle';
    bgmRow.append(bgmLabel, bgmBtn);

    const paintBgm = (): void => {
      const on = isBgmOn();
      bgmBtn.textContent = on ? t('portal.settings.on') : t('portal.settings.off');
      bgmBtn.setAttribute('aria-pressed', String(on));
      bgmBtn.classList.toggle('on', on);
    };
    paintBgm();

    const row = el('div', 'hs-modal-row');
    const closeBtn = el('button', 'hs-btn hs-btn-primary', t('portal.settings.close'));
    row.append(closeBtn);

    panel.append(title, hint, volRow, bgmRow, row);
    panel.insertAdjacentHTML('beforeend', STYLE);

    const changed = (): void => opts.onChange?.();

    vol.addEventListener('input', () => {
      const v = Number(vol.value) / 100;
      volValue.textContent = vol.value;
      setSoundVolume(v);
      setSoundMuted(v <= 0);
      changed();
    });
    // 試し音は**離したときだけ**。⚠️ 動かしている最中に鳴らすと音が折り重なる。
    vol.addEventListener('change', () => {
      if (Number(vol.value) > 0) opts.preview?.();
    });

    bgmBtn.addEventListener('click', () => {
      setBgmOn(!isBgmOn());
      paintBgm();
      changed();
    });

    const done = (): void => {
      close();
      // 🔴 **サーバーへは閉じるときに1回だけ**。⚠️ つまみを動かすたびに投げると、
      //    1回の調整で何十回も往復する（登録済みの人だけが払う無駄）。
      void pushSettingsToServer();
      resolve();
    };

    closeBtn.addEventListener('click', done);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) done();
    });
    document.addEventListener(
      'keydown',
      function onKey(e: KeyboardEvent) {
        if (e.key !== 'Escape') return;
        document.removeEventListener('keydown', onKey);
        done();
      },
    );
  });
}

const STYLE = `<style>
  .hs-set-row {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 2px; border-top: 1px solid rgba(34, 48, 43, .12);
  }
  .hs-set-label { flex: 0 0 5.5em; font-weight: 700; font-size: 14px; }
  /* つまみ。⚠️ タップの下限（44px）を高さで確保する——細い線だと指で掴めない。 */
  .hs-set-range { flex: 1; min-width: 0; height: 44px; accent-color: #0A6E49; }
  .hs-set-value {
    flex: 0 0 2.5em; text-align: right; font-variant-numeric: tabular-nums;
    font-weight: 700; font-size: 14px; color: #5E6C63;
  }
  .hs-set-toggle { flex: 0 0 auto; min-width: 5.5em; }
  .hs-set-toggle.on { border-color: #0A6E49; color: #0A6E49; }
</style>`;
